import { ReactElement, FunctionComponent } from "react";
import uniqueId from "lodash/uniqueId";
import { EMoeToolType } from "logic/src/objects/session/types";
import VideoParser from "logic/src/objects/session/utils/VideoParser";
import {
  IVideoClipInfo,
  EVideoInfoParseStatus,
} from "logic/src/objects/video/types";
import {
  EChatRecvStatus,
  EChatType,
  EStreamDataType,
  IChatRecvMessage,
  IWebSearch,
} from "logic/src/objects/chat/types";

const VIDEO_START_TAG = "1. [";

const VIDEO_START_TAG_MULTI = "1. [视频";

export interface IInfo {
  type: EStreamDataType | "image" | "ref";
  id: string;
}

export interface IVideoEditInfo extends IInfo {
  videoParser: VideoParser;
}

export interface IThinkingInfo extends IInfo {
  text: string;
  startTime: number;
  endTime?: number;
}

export interface IFileListInfo extends IInfo {
  fileList: string[];
  fileType: "image" | "file";
}

export interface ITextInfo extends IInfo {
  textFlow?: string;
  htmlContent?: string;
}

export interface ICodeInfo extends IInfo {
  code: string;
  result?: string;
  files?: string[];
  status?: "coding" | "going";
}

export interface IStatusInfo extends IInfo {
  statusType: string;
  end: boolean;
}

export interface ITextFileInfo extends IInfo {
  type: "image";
  id: string;
  text: string;
  url: string;
  name: string;
  status: "init" | "loading" | "complete";
}

// interface ITextRefInfo extends IInfo {
//   type: "ref";
//   id: string;
//   text: string;
//   webSearch: IWebSearch | null;
//   webSearchRefIndex: number;
//   status: "complete";
// }

export interface IText2ImageInfo extends IInfo {
  text2image: string;
}

export interface IWebSearchInfo extends IInfo {
  weabSearch: IWebSearch[];
}

export type TInfo =
  | IFileListInfo
  | ITextInfo
  | ICodeInfo
  | IStatusInfo
  | ITextFileInfo
  | IVideoEditInfo
  | IText2ImageInfo
  | IThinkingInfo
  | IWebSearchInfo;

export interface IOptions {
  type: EChatType;
}

const getLastType = (info: IInfo) => {
  return info.type;
};

const imageTag = "![IMAGE](";
const inlineImageRegex = /!\[(.*?)\]\((.*?)\)$/;
// const inlineRefRegex = /\[ref_(\d+)\]/;
// const refPrefix = ["[", "[r", "[re", "[ref", "[ref-"];

const replaceEnd = (rawText: string, num: number, value: string) => {
  return rawText.slice(0, -num) + value;
};

class BaseStreamRender {
  history: TInfo[] = [];
  last: TInfo | null = null;
  type: EChatType = EChatType.history;
  nextTextInline: boolean = false;
  end: boolean = false;

  textCpnt: ITextFileInfo | null = null;
  web_searchs: IWebSearch[] = [];

  thinkingInfo: IThinkingInfo | null = null;

  onContentChange: (list: IInfo[], thinkingInfo: IThinkingInfo | null) => void =
    () => {};

  constructor({ type }: IOptions) {
    this.type = type;
    // 待派生类初始化抽象方法后再执行 this.batchWrite(msgList);
    // setTimeout(() => {
    //   this.batchWrite(msgList);
    // }, 1);
    // this.
  }

  setOnContentChange(
    v: (list: IInfo[], thinkingInfo: IThinkingInfo | null) => void,
  ) {
    this.onContentChange = v;
  }

  // getWebSearch() {
  //   const list = this.last ? [...this.history, this.last] : this.history;
  //   const vessel = list.find(
  //     item => item.type === EStreamDataType.web_searcher,
  //   ) as IWebSearchInfo;
  //   return vessel?.weabSearch || [];
  // }

  writeSseText(value: string, isText?: boolean) {
    if (this.last?.type === EStreamDataType.videoEdit) {
      const videoParser = (this.last as IVideoEditInfo).videoParser;

      const result = videoParser.write(value);
      if (result.status === EVideoInfoParseStatus.end) {
        this.pushLast();
        this.last = null;
        if (result.otherStr) {
          result.otherStr.split("").forEach(item => this.writeSseText(item));
        }
        return;
      } else if (result.status === EVideoInfoParseStatus.error) {
        this.pushLast();
        this.last = null;
        if (result.otherStr) {
          result.otherStr
            .split("")
            .forEach(item => this.writeSseText(item, true));
        }
        return;
      } else {
        return;
      }
    }

    if (this.last && getLastType(this.last) === EStreamDataType.text) {
      const lastData = this.last as ITextInfo;

      const textFlow = lastData.textFlow;

      if (textFlow) {
        if (
          !isText &&
          /^[0-9]$/.test(value) &&
          textFlow &&
          (textFlow.endsWith(VIDEO_START_TAG) ||
            textFlow.endsWith(VIDEO_START_TAG_MULTI))
        ) {
          const multiFlag = textFlow.endsWith(VIDEO_START_TAG_MULTI);
          lastData.textFlow = replaceEnd(textFlow, multiFlag ? 6 : 4, "");
          this.pushLast();
          const parser = new VideoParser();
          parser.write(
            multiFlag ? VIDEO_START_TAG_MULTI + value : VIDEO_START_TAG + value,
          );
          this.last = {
            type: EStreamDataType.videoEdit,
            id: uniqueId(EStreamDataType.thinking + "_"),
            videoParser: parser,
          };
          return;
        }

        lastData.textFlow = lastData.textFlow + value;
      }
    } else {
      this.pushLast();
      this.last = {
        type: EStreamDataType.text,
        id: uniqueId(EMoeToolType.text + "_"),
        textFlow: value,
      };
      // if (this.nextTextInline) {
      //   this.last.textFlow?.setLastHtmlTypeToInline();
      //   this.nextTextInline = false;
      // }
    }
  }

  writeThinking(data: IChatRecvMessage) {
    if (data.reasoning_content) {
      const value = data.reasoning_content;
      if (this.thinkingInfo) {
        this.thinkingInfo.text = this.thinkingInfo.text + value;
        this.thinkingInfo = { ...this.thinkingInfo };
        return;
      } else {
        this.thinkingInfo = {
          type: EStreamDataType.thinking,
          id: uniqueId(EStreamDataType.thinking + "_"),
          text: value || "",
          startTime: new Date().getTime(),
        };
      }
    } else {
      if (this.thinkingInfo && !this.thinkingInfo.endTime) {
        this.thinkingInfo.endTime = new Date().getTime();
        this.thinkingInfo = { ...this.thinkingInfo };
      }
    }
  }

  ssetextFlowEnd() {
    if ((this.last as IVideoEditInfo)?.videoParser) {
      const res = (this.last as IVideoEditInfo)?.videoParser.write("", true);
      if (res.status === EVideoInfoParseStatus.error) {
        this.pushLast();
        this.last = null;
        if (res.otherStr) {
          res.otherStr.split("").forEach(item => this.writeSseText(item, true));
        }
      } else {
        this.pushLast();
      }
    }
  }

  _write(data: IChatRecvMessage) {
    if (!data) return;

    this.writeThinking(data);

    console.log("----_write----", data.type, data);

    if (data.type === EStreamDataType.text) {
      // if (this.type === EChatType.stream) {
      const value = data.text || "";
      if (!value) return;

      if (value.length > 1) {
        value.split("").forEach(item => this.writeSseText(item));
        // for (const item of value.split("")) {
        //   await this.writeSseText(item);
        // }
      } else {
        this.writeSseText(value);
      }
      // } else {
      //   this.pushLast();
      //   this.writeTextBlock(data);
      // }
    } else if (data.type === EStreamDataType.text2image) {
      this.ssetextFlowEnd();
      if (this.type === EChatType.history) {
        this.pushLast();
      }
      this.last = {
        type: EStreamDataType.text2image,
        id: uniqueId(EStreamDataType.text2image + "_"),
        text2image: data.text2image,
      };
    } else if (data.type === EStreamDataType.web_searcher) {
      this.ssetextFlowEnd();
      if (this.type === EChatType.history) {
        this.pushLast();
      }
      this.web_searchs = data.web_searcher || [];
      this.last = {
        type: EStreamDataType.web_searcher,
        id: uniqueId(EStreamDataType.web_searcher + "_"),
        weabSearch: data.web_searcher,
      };
      // this.weabSearch = data.web_searcher
    } else if (data.type === EStreamDataType.status) {
      if (data.status === EChatRecvStatus.end) {
        this.end = true;
        this.ssetextFlowEnd();
        if (this.thinkingInfo && !this.thinkingInfo.endTime) {
          this.thinkingInfo.endTime = new Date().getTime();
          this.thinkingInfo = { ...this.thinkingInfo };
        }
        if (
          this.last &&
          getLastType(this.last) === EStreamDataType.status &&
          !(this.last as IStatusInfo).end
        ) {
          (this.last as IStatusInfo).end = true;
        }

        return;
      } else {
        this.pushLast();
        this.last = {
          type: EStreamDataType.status,
          id: uniqueId(EStreamDataType.status + "_"),
          statusType: data.status,
          end: false,
        };
      }
    }
  }

  clear() {
    this.last = null;
    this.history = [];
    this.thinkingInfo = null;
    this.onContentChange([], this.thinkingInfo);
  }

  batchWrite(list: IChatRecvMessage[]) {
    list.forEach(this._write.bind(this));
    // for (const item of list) {
    //   await this._write(item);
    // }
    const result = this.last ? [...this.history, this.last] : this.history;
    this.onContentChange(result, this.thinkingInfo);
  }

  write(data: IChatRecvMessage) {
    this._write(data);
    const result = this.last ? [...this.history, this.last] : this.history;
    this.onContentChange(result, this.thinkingInfo);
  }

  pushLast() {
    if (this.last) {
      if (this.last.type === EStreamDataType.status) {
        (this.last as IStatusInfo).end = true;
      }
      this.history.push(this.last);
    }
  }
}

export default BaseStreamRender;
